import { dashboardErrorMessage } from '../../../../../lib/apiErrorMessages';
import { useAgencyMutations } from '../../hooks';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  toast,
} from '@shigo-ui/ui';
import { dashboardT } from '../../../../../lib/messages';
import type { Agency } from '../../model';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { DiscardChangesDialog } from '../../../../../components/forms/DiscardChangesDialog';

export function AgencyDialog({ agency, onClose }: { agency: Agency | null; onClose: () => void }) {
  const mutations = useAgencyMutations();
  const pending = mutations.create.isPending || mutations.update.isPending;
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const form = useForm<{ name: string }>({ defaultValues: { name: agency?.name ?? '' } });
  const { errors, isDirty } = form.formState;
  const guard = useBusyDialogGuard(pending, () => {
    if (isDirty) setConfirmDiscard(true);
    else onClose();
  });

  return (
    <>
      <Dialog open onOpenChange={guard.onOpenChange}>
        <DialogContent className="max-w-[560px] gap-0 p-0">
          <DialogHeader className="border-border border-b px-5 py-3.5">
            <DialogTitle>{dashboardT(agency ? 'agency.edit' : 'agency.add')}</DialogTitle>
            <DialogDescription>{dashboardT('agency.description')}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              noValidate
              onSubmit={(event) =>
                void form.handleSubmit(async ({ name }) => {
                  if (pending) return;
                  form.clearErrors('root.server');
                  try {
                    if (agency)
                      await mutations.update.mutateAsync({ id: agency.id, name: name.trim() });
                    else await mutations.create.mutateAsync({ name: name.trim() });
                    toast.success(dashboardT(agency ? 'agency.updated' : 'agency.created'));
                    onClose();
                  } catch (error) {
                    form.setError('root.server', {
                      message: dashboardErrorMessage(error, 'agency.saveError'),
                    });
                  }
                })(event)
              }
            >
              <div className="space-y-4 px-5 py-4">
                <div className="space-y-1.5">
                  <Label htmlFor="agency-name">
                    {dashboardT('agency.name')} <span aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="agency-name"
                    required
                    disabled={pending}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'agency-name-error' : undefined}
                    {...form.register('name', {
                      validate: (name) => Boolean(name.trim()) || dashboardT('agency.nameRequired'),
                      maxLength: { value: 255, message: dashboardT('agency.nameTooLong') },
                    })}
                  />
                  {errors.name ? (
                    <p id="agency-name-error" role="alert" className="text-destructive text-sm">
                      {errors.name.message}
                    </p>
                  ) : null}
                </div>
                {errors.root?.server ? (
                  <p role="alert" className="text-destructive text-sm">
                    {errors.root.server.message}
                  </p>
                ) : null}
              </div>
              <DialogFooter className="border-border border-t px-5 py-3">
                <Button type="button" variant="outline" onClick={guard.close} disabled={pending}>
                  {dashboardT('supply.cancel')}
                </Button>
                <Button type="submit" disabled={pending}>
                  {dashboardT('supply.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <DiscardChangesDialog
        open={confirmDiscard}
        onStay={() => {
          setConfirmDiscard(false);
        }}
        onDiscard={onClose}
      />
    </>
  );
}
